import { cx } from '@linaria/atomic';
import { motion } from 'motion/react';
import { Button } from '../button';
import { ContextMenuActions } from '../context-menu';
import { PopoverMenu, PopoverMenuItem, usePopoverMenu } from '../popover';
import { useChatBubbleContext } from './chat';
import { easeInOutVariants } from './chat.styles';
import { ChatStyles } from './chat.styles';
import { PrimaryAction } from './types';

type ActionsProps = {
  primaryActions?: PrimaryAction[];
  trackingId?: string;
  actions?: ContextMenuActions;
  hasError?: boolean;
  onRetry?: () => void;
  retryDisabled?: boolean;
  retryDisabledLabel?: string;
};

type PrimaryActionsListProps = {
  actions: PrimaryAction[];
};

const PrimaryActionsList = ({ actions }: PrimaryActionsListProps) => {
  return (
    <>
      {actions.map((action, index) => (
        <Button
          key={`primary-action-${action.trackingId || index}`}
          className='primary-actions-button'
          hoverLabel={action.label}
          trackingId={action.trackingId}
          onClick={action.onClick}
          iconName={action.icon}
          variant='secondary'
        />
      ))}
    </>
  );
};
export const Actions = ({
  primaryActions,
  trackingId,
  actions,
  hasError,
  onRetry,
  retryDisabled,
  retryDisabledLabel,
}: ActionsProps) => {
  const { direction, hoverProps } = useChatBubbleContext();
  const actionStyles = ChatStyles.actions;
  const { getMenuProps, getTriggerProps, getItemProps } = usePopoverMenu({
    placement: direction === 'outbound' ? 'left-start' : 'right-start',
  });

  return (
    <motion.div
      className={cx('chat-actions', actionStyles)}
      initial='hidden'
      animate='visible'
      exit='hidden'
      variants={easeInOutVariants}
      {...hoverProps}
    >
      {direction === 'inbound' ? (
        // For inbound: more actions, error button, primary actions (reversed)
        <>
          {!hasError && actions && actions.length > 0 && (
            <Button
              className='more-actions-button'
              {...getTriggerProps()}
              trackingId={trackingId}
              iconName='more'
              variant='secondary'
            />
          )}
          {hasError && onRetry && (
            <Button
              className='error-actions-button'
              onClick={onRetry}
              iconName='update'
              variant='secondary'
              disabled={retryDisabled}
              hoverLabel={retryDisabled ? retryDisabledLabel : undefined}
            />
          )}
          {primaryActions && <PrimaryActionsList actions={[...primaryActions].reverse()} />}
        </>
      ) : (
        // For outbound: primary actions, error button, more actions
        <>
          {primaryActions && <PrimaryActionsList actions={primaryActions} />}
          {hasError && onRetry && (
            <Button
              className='error-actions-button'
              onClick={onRetry}
              iconName='update'
              variant='secondary'
              disabled={retryDisabled}
              hoverLabel={retryDisabled ? retryDisabledLabel : undefined}
            />
          )}
          {!hasError && actions && actions.length > 0 && (
            <Button
              className='more-actions-button'
              {...getTriggerProps()}
              trackingId={trackingId}
              iconName='more'
              variant='secondary'
            />
          )}
        </>
      )}
      <PopoverMenu {...getMenuProps()} returnFocus={false}>
        {actions?.map(({ label, onClick, ...action }, index) => (
          <PopoverMenuItem
            key={`chat-item-more-action-${action.trackingId ?? index}`}
            {...getItemProps({
              index,
              onClick,
            })}
            {...action}
          >
            {label}
          </PopoverMenuItem>
        ))}
      </PopoverMenu>
    </motion.div>
  );
};
